import * as React from "react";

// Wide enough for the TTN trips and jobs tables to show without a horizontal
// scrollbar, which the capture would otherwise cut off.
const SNAPSHOT_WIDTH = 1440;

export type TtnSnapshotNodes = { info: HTMLElement; jobs: HTMLElement | null };

/**
 * Lays the TTN tabs out off screen, at full height, for the PDF capture: the
 * visible page shows one tab at a time inside a scroll box. `onReady` gets the
 * tabs' nodes once they have been laid out and painted.
 */
export const TtnSnapshotHost: React.FC<{
  info: React.ReactNode;
  jobs?: React.ReactNode;
  onReady: (nodes: TtnSnapshotNodes) => void;
}> = ({ info, jobs, onReady }) => {
  const infoRef = React.useRef<HTMLDivElement>(null);
  const jobsRef = React.useRef<HTMLDivElement>(null);
  const onReadyRef = React.useRef(onReady);
  onReadyRef.current = onReady;

  React.useEffect(() => {
    // Two frames: the first lets layout settle, the second has it painted.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (infoRef.current) onReadyRef.current({ info: infoRef.current, jobs: jobsRef.current });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      aria-hidden
      // Off screen but still focusable without it; React 18 has no `inert` prop.
      ref={(node) => node?.setAttribute("inert", "")}
      className="pointer-events-none fixed top-0"
      style={{ left: -10_000, width: SNAPSHOT_WIDTH }}
    >
      <div ref={infoRef} className="bg-white p-4">
        {info}
      </div>
      {jobs && (
        <div ref={jobsRef} className="bg-white p-4">
          {jobs}
        </div>
      )}
    </div>
  );
};
